"use client";

import dynamic from "next/dynamic";
import { useCallback, useEffect, useState } from "react";

import BirthToolFormShell from "@/components/tool-shell/BirthToolFormShell";
import { dasamsaFormShellProps } from "./_form-copy";

const DEFAULT_DEFER_MS = 8000;
const ACTIVATE_EVENTS = ["pointerdown", "keydown", "touchstart", "focusin"] as const;

// The shell mirrors the island's BirthDetailsForm word for word (same label
// source, same props), in the island's full-width layout, so the server HTML,
// the chunk-loading frame and the hydrated form read the same in each locale.
const SHELL_CLASS = "mb-10 mt-6";

const ISLANDS = {
  en: dynamic(() => import("./DasamsaChartClient"), {
    ssr: false,
    loading: () => <BirthToolFormShell {...dasamsaFormShellProps("en")} className={SHELL_CLASS} />,
  }),
  hi: dynamic(() => import("./DasamsaChartClient"), {
    ssr: false,
    loading: () => <BirthToolFormShell {...dasamsaFormShellProps("hi")} className={SHELL_CLASS} />,
  }),
};

export default function DeferredDasamsaChartClient({
  deferMs = DEFAULT_DEFER_MS,
  language = "en",
  sourcePath = "/dasamsa",
}: {
  deferMs?: number;
  language?: "en" | "hi";
  sourcePath?: "/dasamsa" | "/hi/dasamsa";
}) {
  const [ready, setReady] = useState(false);
  const activate = useCallback(() => setReady(true), []);

  useEffect(() => {
    if (ready) return;

    const timer = window.setTimeout(activate, deferMs);
    const options: AddEventListenerOptions = { once: true, passive: true };
    for (const eventName of ACTIVATE_EVENTS) {
      window.addEventListener(eventName, activate, options);
    }

    return () => {
      window.clearTimeout(timer);
      for (const eventName of ACTIVATE_EVENTS) {
        window.removeEventListener(eventName, activate);
      }
    };
  }, [activate, deferMs, ready]);

  if (ready) {
    const DasamsaChartClient = ISLANDS[language];
    return <DasamsaChartClient language={language} sourcePath={sourcePath} />;
  }

  return (
    <BirthToolFormShell
      {...dasamsaFormShellProps(language)}
      className={SHELL_CLASS}
      onActivate={activate}
    />
  );
}
